import posthog from 'posthog-js';
export type EventProperties = Record<string, any>;
export interface IdentifyUserOptions {
    distinctId?: string;
    userProperties?: EventProperties;
    eventProperties?: EventProperties;
}

// PostHog's own anonymous ids are UUIDs; the app identifies people by their account id, which has no dashes.
const isIdentified = (distinctId: string | undefined): boolean => distinctId !== undefined && !distinctId.includes('-');

export const analytics = {

    capture(event: string, properties?: EventProperties): void {
        try {
            posthog?.capture(event, properties);
        } catch (e) {
            console.error('[Analytics] capture failed:', e);
        }
    },

    async identifyUser({
        distinctId,
        userProperties = {},
        eventProperties = {},
    }: IdentifyUserOptions): Promise<void> {
        try {
            const current = posthog?.get_distinct_id();
            if (distinctId !== undefined && current === distinctId) return;
            // Someone else was identified here: their events stay theirs, and this person's start fresh.
            if (distinctId !== undefined && isIdentified(current)) posthog.reset();

            if (typeof posthog !== 'undefined') {
                if (distinctId) {
                    posthog.identify(distinctId, userProperties, eventProperties);
                } else {
                    posthog.people.set(userProperties);
                }

                // Register super properties — attached to every subsequent event
                posthog.register({
                    internalTeam: userProperties?.isStaff ?? false,
                });
            }
        } catch (error) {
            console.error('[Analytics] identifyUser failed:', error);
        }
    },

    identify(distinctId: string, properties?: EventProperties): void {
        try {
            posthog?.identify(distinctId, properties);
        } catch (e) {
            console.error('[Analytics] identify failed:', e);
        }
    },

    group(groupType: string, groupKey: string, properties?: EventProperties): void {
        try {
            posthog?.group(groupType, groupKey, properties);
        } catch (e) {
            console.error('[Analytics] group failed:', e);
        }
    },

    reset(): void {
        try {
            posthog?.reset();
        } catch (e) {
            console.error('[Analytics] reset failed:', e);
        }
    },

    register(properties: EventProperties): void {
        try {
            posthog?.register(properties);
        } catch (e) {
            console.error('[Analytics] register failed:', e);
        }
    },
    isFeatureEnabled(flag: string): boolean | undefined {
        return posthog?.isFeatureEnabled(flag);
    },
    getFeatureFlag(flag: string): string | boolean | undefined {
        return posthog?.getFeatureFlag(flag);
    },
    getInstance() {
        return posthog;
    },
};
